﻿import { FC } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, Button, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { trackConsultationClick } from '../analytics/events';
import { PageProps } from './HomePage';

export const AboutPage: FC<PageProps> = ({ onNavigate }) => {
  const handleConsultation = (intent: string) => {
    trackConsultationClick(intent, 'about_page');
    onNavigate(ROUTES.CONTACT);
  };

  const coreValues = [
    {
      title: 'Practical, Real-World Advice',
      desc: 'We eschew generic motivational platitudes in favor of tangible deliverables: ATS-aligned resumes, objective skill-gap audits, and scripted interview strategies.'
    },
    {
      title: 'Zero Synthetic Claims',
      desc: 'We maintain complete integrity in our positioning. We do not invent statistics, publish fabricated reviews, or make unrealistic employment guarantees.'
    },
    {
      title: 'Holistic Career & Business Support',
      desc: 'By understanding both candidate realities and employer talent requirements, we bridge the gap between individual career ambition and organizational performance.'
    }
  ];

  return (
    <>
      <SEOHead
        title="About Connect Source — Nilesh Mishra & Our Mission"
        description="Learn about Connect Source and Founder Nilesh Mishra — Career Transformation Coach, Certified HRM, and Author of 'The Balanced Empire'. Grounded career and HR advisory."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="xl">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <Badge variant="primary" style={{ marginBottom: '1rem' }}>Our Purpose & Mission</Badge>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
              Bridging Ambition With Execution
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1.25rem', lineHeight: 1.65 }}>
              Connect Source was founded to provide professionals with structured, disciplined career transformation guidance and companies with agile, high-integrity HR and business support.
            </p>
          </div>
        </Container>
      </section>

      {/* Founder Profile Section */}
      <Section title="Founder & Leadership" subtitle="Professional background anchored in human resource management and career coaching.">
        <Container size="lg">
          <Card style={{ padding: '2.5rem', backgroundColor: '#ffffff' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '2.5rem', alignItems: 'center' }}>
              <div>
                <div
                  style={{
                    width: '6rem',
                    height: '6rem',
                    borderRadius: 'var(--radius-xl)',
                    backgroundColor: 'var(--color-navy-dark)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.25rem',
                    fontWeight: 800,
                    marginBottom: '1rem'
                  }}
                >
                  NM
                </div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                  Nilesh Mishra
                </h2>
                <div style={{ fontSize: '1.05rem', color: 'var(--color-brand-primary)', fontWeight: 600, marginTop: '0.25rem' }}>
                  Founder — Connect Source
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  <Badge variant="neutral">Career Transformation Coach</Badge>
                  <Badge variant="teal">Certified HRM (CHRM)</Badge>
                  <Badge variant="primary">Author of "The Balanced Empire"</Badge>
                </div>
              </div>

              <div>
                <p style={{ fontSize: '1.05rem', color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                  Nilesh Mishra brings a disciplined, structured approach to career acceleration and human resource consulting. As a Certified Human Resource Professional (CHRM) and author of <em>"The Balanced Empire"</em>, Nilesh specializes in cutting through corporate hiring ambiguity to help professionals position their true capabilities effectively.
                </p>
                <p style={{ fontSize: '0.95rem', color: 'var(--color-text-secondary)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                  Connect Source emerged from a clear observation: job seekers often struggle not because of lack of talent, but because of poor narrative positioning, outdated ATS formats, and unstructured job search tactics. Concurrently, growing startups need reliable, ethical HR support without cumbersome overhead.
                </p>
                <Button variant="outline" size="md" onClick={() => handleConsultation('founder_inquiry')}>
                  Connect with Nilesh Mishra →
                </Button>
              </div>
            </div>
          </Card>
        </Container>
      </Section>

      {/* Principles & Methodology */}
      <Section
        title="Our Operating Principles"
        subtitle="The standards that guide every client interaction and service deliverable."
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', borderBottom: '1px solid var(--color-border-subtle)' }}
      >
        <div className="cs-grid-3">
          {coreValues.map((v, i) => (
            <Card key={i} style={{ padding: '1.75rem' }}>
              <CardTitle style={{ fontSize: '1.15rem' }}>{v.title}</CardTitle>
              <CardDescription style={{ marginTop: '0.6rem', lineHeight: 1.6, fontSize: '0.925rem' }}>
                {v.desc}
              </CardDescription>
            </Card>
          ))}
        </div>
      </Section>

      {/* Dual CTA */}
      <section style={{ padding: '4.5rem 0', backgroundColor: 'var(--color-bg-canvas)' }}>
        <Container size="lg">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            <Card style={{ padding: '2rem', textAlign: 'center' }}>
              <Badge variant="primary" style={{ marginBottom: '0.75rem' }}>For Professionals</Badge>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-navy-dark)' }}>Accelerate Your Career</h3>
              <p style={{ fontSize: '0.925rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem', lineHeight: 1.55 }}>
                Explore 1-on-1 coaching programs and self-paced digital toolkits designed for immediate clarity and interview readiness.
              </p>
              <div style={{ marginTop: '1.5rem' }}>
                <Button variant="primary" onClick={() => onNavigate(ROUTES.CAREER_COACHING)}>
                  Explore Career Coaching
                </Button>
              </div>
            </Card>

            <Card style={{ padding: '2rem', textAlign: 'center' }}>
              <Badge variant="teal" style={{ marginBottom: '0.75rem' }}>For Organizations</Badge>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-navy-dark)' }}>Strengthen Your HR</h3>
              <p style={{ fontSize: '0.925rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem', lineHeight: 1.55 }}>
                Discover how flexible recruitment, HR operational frameworks, and executive virtual assistance support your growth.
              </p>
              <div style={{ marginTop: '1.5rem' }}>
                <Button variant="secondary" onClick={() => onNavigate(ROUTES.BUSINESS_SOLUTIONS)}>
                  Explore Business Solutions
                </Button>
              </div>
            </Card>
          </div>
        </Container>
      </section>
    </>
  );
};


